import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem("auth_token");
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const res = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUser(res.data);
    } catch {
      localStorage.removeItem("auth_token");
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (loginValue, password) => {
    const res = await axios.post(`${API}/auth/login`, { login: loginValue, password });
    const { access_token, user: userData } = res.data;
    localStorage.setItem("auth_token", access_token);
    setUser(userData);
    return userData;
  };

  const register = async (name, email, password) => {
    const res = await axios.post(`${API}/auth/register`, { name, email, password });
    const { access_token, user: userData } = res.data;
    localStorage.setItem("auth_token", access_token);
    setUser(userData);
    return userData;
  };

  const loginWithGoogle = async (sessionId) => {
    const res = await axios.post(`${API}/auth/google`, { session_id: sessionId });
    const { access_token, user: userData } = res.data;
    localStorage.setItem("auth_token", access_token);
    setUser(userData);
    return userData;
  };

  const acceptExternalToken = async (token) => {
    localStorage.setItem("auth_token", token);
    try {
      const res = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUser(res.data);
      return res.data;
    } catch (error) {
      localStorage.removeItem("auth_token");
      setUser(null);
      throw error;
    }
  };

  const logout = async () => {
    const token = localStorage.getItem("auth_token");
    try {
      await axios.post(`${API}/auth/logout`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch { /* ignore */ }
    localStorage.removeItem("auth_token");
    setUser(null);
  };

  const refreshUser = async () => {
    await checkAuth();
  };

  return (
    <AuthContext.Provider value={{ user, loading, isAuthenticated: !!user, login, register, loginWithGoogle, acceptExternalToken, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
